import { isBulkEnqueueListLagState, shouldClearProcessingIndicator } from '@/lib/bulkProcessQueueSync';
import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import type { Application } from '@/types';

/** Bulk batch row still waiting for a slot — not the same as post-pipeline settled. */
export function isBulkBatchRowWaitingForSlot(
  app: Pick<Application, 'state'> | undefined,
  inBulkTarget: boolean,
): boolean {
  return inBulkTarget && !!app && isBulkEnqueueListLagState(app.state);
}

function isInActiveBulkTargetSet(
  companyId: string | undefined,
  applicationId: string,
): boolean {
  if (!companyId || !applicationId) return false;
  const banner = useBulkProcessBannerStore.getState();
  if (
    banner.phase === 'active'
    && banner.companyId === companyId
    && banner.targetIds.includes(applicationId)
  ) {
    return true;
  }
  const loaded = loadBulkProcessQueue(companyId);
  return loaded.targetIds.includes(applicationId);
}

/** True while this row belongs to the active bulk batch (banner store or sessionStorage). */
export function isBulkBatchTargetId(
  companyId: string | undefined,
  applicationId: string,
): boolean {
  return isInActiveBulkTargetSet(companyId, applicationId);
}

/** True while this application is part of the active bulk screening batch (any page / hook instance). */
export function isApplicationInActiveBulkQueue(
  companyId: string | undefined,
  applicationId: string,
): boolean {
  if (!isInActiveBulkTargetSet(companyId, applicationId)) return false;
  const loaded = loadBulkProcessQueue(companyId!);
  return loaded.processingIds.has(applicationId) || loaded.stoppingIds.has(applicationId);
}

/** Best-effort bulk batch scope: hook ref → sessionStorage → banner store. */
export function resolveBulkTargetIds(
  companyId: string,
  refTargetIds: readonly string[],
): string[] {
  if (refTargetIds.length > 0) return [...refTargetIds];
  const loaded = loadBulkProcessQueue(companyId);
  if (loaded.targetIds.length > 0) return loaded.targetIds;
  const banner = useBulkProcessBannerStore.getState();
  if (banner.phase === 'active' && banner.companyId === companyId && banner.targetIds.length > 0) {
    return banner.targetIds;
  }
  return [];
}

/**
 * Processing tab + in-flight list — includes bulk batch rows even when processingIds was reconciled empty.
 */
export function isApplicationBulkBatchInFlight(
  app: Pick<Application, 'id' | 'state'>,
  companyId: string | undefined,
  processingIds: ReadonlySet<string>,
  stoppingIds: ReadonlySet<string>,
): boolean {
  if (!companyId) return false;
  if (processingIds.has(app.id) || stoppingIds.has(app.id)) return true;
  if (!isInActiveBulkTargetSet(companyId, app.id)) return false;

  if (app.state === 'PIPELINE_RUNNING' || isBulkEnqueueListLagState(app.state)) {
    return true;
  }
  if (shouldClearProcessingIndicator(app.state) && !isBulkEnqueueListLagState(app.state)) {
    return false;
  }
  return useBulkProcessBannerStore.getState().phase === 'active';
}
